import { Bolt, ChangeCircle, Edit, Power } from "@mui/icons-material";
import { Box, Skeleton } from "@mui/material";
import IconButton from "components/icon-button";
import { useProject } from "contexts/project";
import { IProjectConnectorDTO } from "data/dtos/automation/i-project-dto";
import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "services/api";
import { dayjs } from "utils/custom-date";
import ChipConnectorText from "utils/project/chip-connector-text";
import ChipStatus from "utils/project/chip-status";
import ModalChangeOutput from "../modal-change-output";
import { useStyles } from "./style";

export interface IOptionToChange {
  id: string;
  name: string;
}

interface ConnectorCardProps {
  name: string;
  type: string;
  status: string;
  lastUpdate: Date | null;
  connector?: IProjectConnectorDTO;
}

const ProjectViewConnectorAndAppCard: React.FC<ConnectorCardProps> = ({
  type,
  name,
  status,
  connector,
  lastUpdate,
}) => {
  const { data: project, refreshData } = useProject();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [optionsToChange, setOptionsToChange] = useState<IOptionToChange[]>([]);
  const [isLoadingChange, setIsLoadingChange] = useState<boolean>(false);

  const classes = useStyles();
  const history = useHistory();

  const waitingTextList = [
    "Em_Espera",
    "not_granted",
    "not_triggered",
    "not_authorized",
  ];
  const successTextList = ["Finalizado", "success", "granted", "authorized"];

  const handleFormatDate = (date: string | Date) =>
    dayjs(date).format("DD/MM/YYYY - HH:mm:ss");

  const isAbleToChange = type == "google-drive" || type == "email";
  const haveExternalConnector = type == "google-drive";

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const handleConnect = (type: string) => {
    if (type == "google-drive") {
      history.push(`/projects/${project.id}/setup/${connector!.key}`);
    }
  };

  const loadOptionsToChange = async () => {
    try {
      const { data } = await api.post("/connectors/select", {
        connectionTypeId: connector!.connectorTypeId,
      });

      setOptionsToChange(data.data);
    } catch (error) {
      console.log("Error ao carregar opções de mudança de conector");
    }
  };

  const handleChangeConnector = async (connectorId: string) => {
    try {
      setIsLoadingChange(true);
      await new Promise((resolve) => setTimeout(resolve, 10000));
      await api.post(`/project-connectors/change/${project.id}`, {
        connectorId: connectorId,
        position: connector!.position,
      });

      handleClose();
      await refreshData();
    } catch (error) {
      console.log("Error ao mudar de conector");
    } finally {
      setIsLoadingChange(false);
    }
  };

  useEffect(() => {
    if (connector && connector.connectorTypeName == "output") {
      loadOptionsToChange();
    }
  }, []);

  return (
    <Box className={classes.root}>
      {isLoadingChange ? (
        <div className={classes.loading}>
          <Skeleton variant="rounded" width={"40%"} height={50} />
          <Skeleton variant="rounded" width={"31%"} height={50} />
          <Skeleton variant="rounded" width={"29%"} height={50} />
        </div>
      ) : (
        <table className={classes.tableBase}>
          <thead className={classes.tableHeader}>
            <tr>
              <th></th>
              <th className={classes.tableName}>Conector</th>
              <th className={classes.tableUpdate}>Ultima Atualização</th>
              <th className={classes.tableStatus}>Status</th>
              <th></th>
            </tr>
          </thead>

          <tbody className={classes.tableBody}>
            <tr>
              <td className={classes.tableIcon}>
                <Box sx={{ mt: "-1rem" }}>
                  <IconButton
                    icon={Bolt}
                    isDisabled={waitingTextList.includes(status)}
                    onClick={() => {}}
                  />
                </Box>
              </td>

              <td className={classes.tableName}>
                <ChipConnectorText name={name} type={type} />
              </td>

              <td className={classes.tableUpdate}>
                {lastUpdate ? handleFormatDate(lastUpdate) : "-"}
              </td>

              <td className={classes.tableStatus}>
                <ChipStatus
                  text={status}
                  messages={{
                    failed: "Erro na Conexão",
                    success: "Conectado",
                    processing: "Processando",
                    waiting: "Não Conectado",
                  }}
                />
              </td>

              <td className={classes.tableActions}>
                <Box
                  sx={{
                    mt: "-1rem",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                  }}
                >
                  {isAbleToChange && (
                    <IconButton
                      isDisabled={false}
                      icon={ChangeCircle}
                      onClick={handleOpen}
                      tooltip="Mudar Conector"
                    />
                  )}

                  {haveExternalConnector && (
                    <>
                      {successTextList.includes(status) ? (
                        <IconButton
                          icon={Edit}
                          onClick={() => handleConnect(type)}
                          tooltip="Editar"
                        />
                      ) : (
                        <IconButton
                          icon={Power}
                          onClick={() => handleConnect(type)}
                          tooltip="Connectar"
                        />
                      )}
                    </>
                  )}
                </Box>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <ModalChangeOutput
        open={openModal}
        onClose={handleClose}
        onSelect={(id: string) => handleChangeConnector(id)}
        options={optionsToChange.filter(
          (option) => option.name != connector!.name
        )}
      />
    </Box>
  );
};

export default ProjectViewConnectorAndAppCard;
